/**
 * Rich text as data rather than HTML. A document is a list of blocks:
 *
 *   { type: 'paragraph', children: Run[] }
 *   { type: 'bullet' | 'numbered', items: Run[][] }
 *
 * and a run is `{ text, bold?, italic?, underline? }`. The editor produces this
 * from its own DOM and RichTextView turns it back into React elements, so no
 * markup ever travels to the API or gets injected into the page — there is
 * nothing to sanitise, only a shape to check (the API does that too).
 */

const MARK_TAGS = {
  B: 'bold', STRONG: 'bold',
  I: 'italic', EM: 'italic',
  U: 'underline',
};

const LIST_TAGS = { UL: 'bullet', OL: 'numbered' };
const BLOCK_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);

const sameMarks = (a, b) => !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.underline === !!b.underline;

/* A run carries only the marks that are on, so equal formatting compares equal
   and the stored document stays small. */
function makeRun(text, marks) {
  const run = { text };
  if (marks.bold) run.bold = true;
  if (marks.italic) run.italic = true;
  if (marks.underline) run.underline = true;
  return run;
}

function pushRun(runs, run) {
  if (!run.text) return;
  const last = runs[runs.length - 1];
  if (last && last.text !== '\n' && run.text !== '\n' && sameMarks(last, run)) last.text += run.text;
  else runs.push(run);
}

/* Inline content of a node as runs. A <br> becomes a '\n' run, which the block
   walker below splits paragraphs on. Some browsers mark formatting with inline
   styles rather than tags, so those count too. */
function collectRuns(node, marks, runs) {
  if (node.nodeType === 3) {
    pushRun(runs, makeRun(node.nodeValue.replace(/​/g, ''), marks));
    return;
  }
  if (node.nodeType !== 1) return;
  if (node.tagName === 'BR') { runs.push({ text: '\n' }); return; }

  const next = { ...marks };
  if (MARK_TAGS[node.tagName]) next[MARK_TAGS[node.tagName]] = true;
  const st = node.style;
  if (st) {
    if (st.fontWeight === 'bold' || Number(st.fontWeight) >= 600) next.bold = true;
    if (st.fontStyle === 'italic') next.italic = true;
    if ((st.textDecoration || st.textDecorationLine || '').includes('underline')) next.underline = true;
  }
  node.childNodes.forEach(child => collectRuns(child, next, runs));
}

/* Runs split at each line break, one paragraph per line. */
function linesOf(runs) {
  const lines = [[]];
  for (const run of runs) {
    if (run.text === '\n') lines.push([]);
    else lines[lines.length - 1].push(run);
  }
  return lines;
}

/* Walks the editor's children into blocks. Inline nodes between block elements
   are gathered up and become paragraphs of their own — that is how the first
   line typed into an empty editor arrives. */
function walkBlocks(parent, blocks) {
  let inline = [];
  const flush = () => {
    if (!inline.length) return;
    const runs = [];
    inline.forEach(n => collectRuns(n, {}, runs));
    // A trailing <br> only holds a line open; it isn't a line of its own.
    if (runs.length && runs[runs.length - 1].text === '\n') runs.pop();
    linesOf(runs).forEach(children => blocks.push({ type: 'paragraph', children }));
    inline = [];
  };

  parent.childNodes.forEach(node => {
    const tag = node.nodeType === 1 ? node.tagName : null;
    if (tag && LIST_TAGS[tag]) {
      flush();
      const items = [];
      node.childNodes.forEach(li => {
        if (li.nodeType !== 1) return;
        const runs = [];
        const nested = [];
        // A list nested in an item is flattened into the same list, after the
        // item it sits in: the editor offers one level only.
        li.childNodes.forEach(child => {
          if (child.nodeType === 1 && LIST_TAGS[child.tagName]) {
            child.querySelectorAll('li').forEach(inner => {
              const innerRuns = [];
              collectRuns(inner, {}, innerRuns);
              nested.push(innerRuns.filter(r => r.text !== '\n'));
            });
          } else collectRuns(child, {}, runs);
        });
        items.push(runs.filter(r => r.text !== '\n'), ...nested);
      });
      blocks.push({ type: LIST_TAGS[tag], items });
    } else if (tag && BLOCK_TAGS.has(tag)) {
      flush();
      walkBlocks(node, blocks);
    } else {
      inline.push(node);
    }
  });
  flush();
}

const isBlankRuns = (runs) => runs.every(r => !r.text.trim());

/* The editor's DOM as a document. Leading and trailing empty lines are dropped,
   and so are empty list items — the browser leaves one behind when a list is
   switched off. */
export function serializeEditor(root) {
  const blocks = [];
  walkBlocks(root, blocks);
  const cleaned = blocks
    .map(b => (b.type === 'paragraph' ? b : { ...b, items: b.items.filter(items => !isBlankRuns(items)) }))
    .filter(b => b.type === 'paragraph' || b.items.length);
  while (cleaned.length && cleaned[0].type === 'paragraph' && isBlankRuns(cleaned[0].children)) cleaned.shift();
  while (cleaned.length && cleaned[cleaned.length - 1].type === 'paragraph'
    && isBlankRuns(cleaned[cleaned.length - 1].children)) cleaned.pop();
  return cleaned;
}

const runsText = (runs) => runs.map(r => r.text).join('');

/* The document's text, one line per paragraph or list item. */
export function plainText(blocks) {
  return (blocks || [])
    .flatMap(b => (b.type === 'paragraph' ? [runsText(b.children)] : b.items.map(runsText)))
    .join('\n');
}

/* Characters counted against the limit: what was typed, not the line breaks
   between blocks — the same figure the counter shows. */
export function textLength(blocks) {
  return (blocks || []).reduce((n, b) => n + (b.type === 'paragraph'
    ? runsText(b.children).length
    : b.items.reduce((m, item) => m + runsText(item).length, 0)), 0);
}

export const isBlankDocument = (blocks) => !plainText(blocks).trim();
